/**
 * Whether the current session may manage the server host.
 *
 * Host-management RPCs (server settings, provider maintenance, self-update,
 * process signals) require the administrative `access:write` scope on the
 * server (`apps/server/src/auth/RpcAuthorization.ts`). Hosted WorkOS members
 * hold only the standard client scopes, so the controls behind those RPCs
 * must not be offered to them: a request would be refused and the optimistic
 * UI would silently revert. The desktop app talks to its own local server
 * through the bridge and is always the operator there.
 */
import { AuthAccessWriteScope, AuthAdministrativeScopes } from "@amoreg/contracts";

import type { EnvironmentId } from "@amoreg/contracts";

import { usePrimarySessionState } from "~/environments/primary/sessionState";
import { useEnvironmentSessionState } from "~/state/session";

export const HOST_MANAGED_HINT = "Managed by the server operator.";

/**
 * Pure rule shared by the hook and its tests. `scopes === null` means the
 * session state is not known yet (loading, or a failed read): the controls
 * stay visible rather than flickering in later, because the server refuses
 * the RPC either way — this gate is about not offering what will be refused,
 * never the authorization itself.
 */
export function canManageHost(input: {
  readonly scopes: ReadonlyArray<string> | null;
  readonly isDesktop: boolean;
}): boolean {
  if (input.isDesktop) return true;
  if (input.scopes === null) return true;
  return input.scopes.includes(AuthAccessWriteScope);
}

export function useCanManageHost(): boolean {
  const desktopBridge = typeof window !== "undefined" ? window.desktopBridge : undefined;
  const session = usePrimarySessionState();
  const scopes = desktopBridge
    ? AuthAdministrativeScopes
    : session.data?.authenticated
      ? (session.data.scopes ?? null)
      : null;
  return canManageHost({ scopes, isDesktop: Boolean(desktopBridge) });
}

/**
 * The same capability for one specific environment, read from that
 * environment's own `/api/auth/session` state: a secondary server the person
 * paired themselves carries its own scopes, which may differ from the
 * primary session's. Same rules as `useCanManageHost` otherwise.
 */
export function useCanManageEnvironmentHost(environmentId: EnvironmentId): boolean {
  const desktopBridge = typeof window !== "undefined" ? window.desktopBridge : undefined;
  const session = useEnvironmentSessionState(environmentId);
  const scopes = desktopBridge
    ? AuthAdministrativeScopes
    : session.data?.authenticated
      ? (session.data.scopes ?? null)
      : null;
  return canManageHost({ scopes, isDesktop: Boolean(desktopBridge) });
}
